//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//* Import PaginationData type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type Discipline,
  type DisciplineDetails,
  type DisciplineResponse,
} from "./types/types";

// API methods for Discipline operations
export const disciplineAPI = {
  // Fetch all Disciplines with optional search and pagination
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<DisciplineResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/disciplines?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          disciplines: data.disciplines || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching Disciplines:", error);
        throw new Error("Failed to fetch Disciplines");
      });
  },

  // Fetch a Discipline by ID
  getById: async function (
    id: string,
    token: string
  ): Promise<DisciplineDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/disciplines/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Error fetching discipline:", error);
        throw new Error("Failed to fetch Discipline");
      });
  },

  // Create a new Discipline
  create: async function (
    data: { name: string },
    token: string
  ): Promise<Discipline> {
    return api
      .request({
        url: `${API_URI}/admin/private/disciplines`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating discipline:", error, data);
        throw new Error("Failed to create Discipline");
      });
  },

  // Update an existing Discipline
  update: async function (
    id: string,
    data: { name: string },
    token: string
  ): Promise<Discipline> {
    return api
      .request({
        url: `${API_URI}/admin/private/disciplines/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating discipline:", error);
        throw new Error("Failed to update Discipline");
      });
  },

  // Delete an Discipline by ID
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/disciplines/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting discipline:", error);
        throw new Error("Failed to delete Discipline");
      });
  },
};
